import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { SiteHeader } from '../components/site-header'
import { CoreOfferings } from '../components/home/core-offerings'
import { StartJourneyCta } from '../components/home/start-journey-cta'
import { Button } from '../components/ui/button'
import { HeroAnimation } from '../components/home/hero-animation'
import { motion } from 'framer-motion'
import { createNavigationButton, createEngagementButton } from '../lib/analytics-factory'
import { ArrowRight, Clock, Brain, CheckCircle, Zap, Database, BarChart } from 'lucide-react'
import { safeTrackEvent } from '../lib/analytics-utils'
import { EventCategory } from '../lib/analytics'
import { METADATA } from '../components/meta/metadata'

export default function HomePage() {
  const location = useLocation();
  
  // Set metadata for the page
  useEffect(() => {
    document.title = METADATA.home.title;
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', METADATA.home.description);
  }, []);
  
  const handleNavClick = (differentiator: string, destination: string) => {
    safeTrackEvent({
      category: EventCategory.Navigation,
      label: `nav_home_${differentiator.toLowerCase().replace(/ /g, '_')}`,
      context: {
        sourcePage: location.pathname,
        destinationPage: destination,
        section: "hero_differentiators"
      }
    });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="absolute inset-0 z-0">
        <img
          src="/images/backgrounds/flickr-3043814449.jpg"
          alt="AI innovation and knowledge engineering"
          className="object-cover w-full h-full"
          style={{ 
            opacity: 0.45, 
            filter: "contrast(1.1) grayscale(0.9) brightness(0.9)" 
          }}
        />
        {/* Enhanced gradient overlay with more opacity at top */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/60 to-background" />
      </div>
      
      <SiteHeader />
      <main className="flex-1 pt-16 relative z-10">
        {/* Hero Section - Full width - Keep this updated part */}
        <section className="container relative min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center py-10 sm:py-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center text-center gap-2 sm:gap-4 w-full max-w-4xl mx-auto"
          >
            <HeroAnimation />
            <div className="space-y-8">
              <motion.h1
                className="text-2xl font-light tracking-tight sm:text-3xl md:text-4xl lg:text-5xl text-foreground/90"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                AI Innovation & Knowledge Engineering
              </motion.h1>
              <motion.p
                className="mx-auto max-w-[700px] text-lg sm:text-xl text-muted-foreground leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Syllepsis helps organisations move from AI experiments to sustainable AI capability. We assess where AI creates genuine value, structure the knowledge it needs and build systems that deliver measurable results.
              </motion.p>
            </div>
            
            {/* Key differentiators with icons */}
            <motion.div
              className="grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8 mt-8 mb-8 sm:mb-6 w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link
                to="/services/knowledge-engineering"
                className="flex flex-col items-center hover:scale-105 transition-transform"
                onClick={() => handleNavClick("Knowledge Architecture", "/services/knowledge-engineering")}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Database className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-center">
                  <p className="font-medium">Knowledge</p>
                  <p className="font-medium">Architecture</p>
                </div>
              </Link>
              <Link
                to="/services/partner-ai"
                className="flex flex-col items-center hover:scale-105 transition-transform"
                onClick={() => handleNavClick("Partner AI", "/services/partner-ai")}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Brain className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-center">
                  <p className="font-medium">Partner AI</p>
                  <p className="font-medium">Systems</p>
                </div>
              </Link>
              <Link
                to="/services/ai-opportunity"
                className="flex flex-col items-center hover:scale-105 transition-transform"
                onClick={() => handleNavClick("Strategic Assessment", "/services/ai-opportunity")}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <BarChart className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-center">
                  <p className="font-medium">Strategic</p>
                  <p className="font-medium">Assessment</p>
                </div>
              </Link>
              <Link
                to="/services/ai-kickstarter"
                className="flex flex-col items-center hover:scale-105 transition-transform"
                onClick={() => handleNavClick("Rapid Results", "/services/ai-kickstarter")}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Zap className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-center">
                  <p className="font-medium">Rapid</p>
                  <p className="font-medium">Results</p>
                </div>
              </Link>
            </motion.div>
            
            {/* Rapid Implementation Banner */}
            <motion.div
              className="w-full mt-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="flex items-center justify-center gap-3 py-3 px-6 rounded-lg bg-primary/5 border border-primary/10">
                <Clock className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <p className="font-medium text-primary">Practical AI transformation. Measurable results.</p>
              </div>
            </motion.div>
          </motion.div>
        </section>

        <CoreOfferings />
        <StartJourneyCta />
      </main>
    </div>
  )
}